import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "choice", "credential", "savedSelect", "savedCvv", "newFields",
    "holder", "number", "expiration", "cvv", "error", "cardLast4",
    "useForRecurring", "recurringHelp", "storeCard", "chargeRecurringCard"
  ]

  static values = { publicKey: String, recurringCardId: String }

  connect() {
    this.form = this.element.closest("form")
    this.submitHandler = this.prepare.bind(this)
    this.form?.addEventListener("submit", this.submitHandler)
    this.loadSdk()
    this.toggle()
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.submitHandler)
  }

  toggle() {
    const subsequent = this.hasChargeRecurringCardTarget && this.chargeRecurringCardTarget.checked
    if (this.hasUseForRecurringTarget) {
      if (subsequent) this.useForRecurringTarget.checked = false
      this.useForRecurringTarget.disabled = subsequent
    }
    const recurring = this.hasUseForRecurringTarget && this.useForRecurringTarget.checked
    if (this.hasSavedSelectTarget) {
      for (const option of this.savedSelectTarget.options) {
        if (option.hasAttribute("value") && option.value) {
          option.disabled = subsequent ? option.value !== this.recurringCardIdValue : recurring && option.dataset.recurringInitial !== "true"
        }
      }
      if (this.savedSelectTarget.selectedOptions[0]?.disabled) this.savedSelectTarget.value = ""
      if (subsequent) this.savedSelectTarget.value = this.recurringCardIdValue
      this.savedSelectTarget.disabled = false
    }
    if (this.hasRecurringHelpTarget) this.recurringHelpTarget.classList.toggle("d-none", !recurring)
    if (this.hasStoreCardTarget) {
      if (recurring) this.storeCardTarget.checked = true
      this.storeCardTarget.disabled = recurring
    }
    const saved = this.hasSavedSelectTarget && this.savedSelectTarget.value !== ""
    const selectedCardId = saved ? this.savedSelectTarget.value : ""
    const cardChanged = this.selectedCardId !== selectedCardId
    this.selectedCardId = selectedCardId
    this.choiceTarget.value = saved ? "saved" : "new"
    this.newFieldsTarget.classList.toggle("d-none", saved)
    this.cvvTarget.disabled = saved
    this.cvvTarget.required = !saved
    if (this.hasSavedCvvTarget) {
      this.savedCvvTarget.classList.toggle("d-none", !saved || subsequent)
      const savedCvv = this.savedCvvTarget.querySelector("input")
      savedCvv.disabled = !saved || subsequent
      savedCvv.required = saved && !subsequent
      if (cardChanged || !saved || subsequent) savedCvv.value = ""
    }
    this.credentialTarget.value = ""
    if (this.hasCardLast4Target) this.cardLast4Target.value = ""
  }

  prepare(event) {
    this.hideError()
    if (this.choiceTarget.value === "saved") {
      if (this.hasChargeRecurringCardTarget && this.chargeRecurringCardTarget.checked &&
          this.savedSelectTarget.value === this.recurringCardIdValue) return
      const savedCvv = this.savedCvvTarget.querySelector("input")
      const code = savedCvv.value.replace(/\D/g, "")
      if (!/^\d{3,4}$/.test(code)) {
        event.preventDefault()
        event.stopPropagation()
        this.showError("Informe o CVV de 3 ou 4 dígitos do cartão salvo.")
        return
      }
      savedCvv.value = code
      return
    }

    try {
      if (!this.publicKeyValue) throw new Error("Chave pública do PagBank não configurada.")
      if (!window.PagSeguro?.encryptCard) throw new Error("Serviço seguro do PagBank ainda não carregou.")

      const { month, year } = this.parseExpiration(this.expirationTarget.value)
      const securityCode = this.cvvTarget.value.replace(/\D/g, "")
      if (!/^\d{3,4}$/.test(securityCode)) {
        throw new Error("Informe o CVV de 3 ou 4 dígitos do cartão.")
      }

      const cardNumber = this.numberTarget.value.replace(/\D/g, "")
      if (cardNumber.length < 12 || cardNumber.length > 19) {
        throw new Error("Informe um número de cartão válido.")
      }

      const result = window.PagSeguro.encryptCard({
        publicKey: this.publicKeyValue,
        holder: this.holderTarget.value,
        number: cardNumber,
        expMonth: month,
        expYear: year,
        securityCode
      })

      if (result.hasErrors || !result.encryptedCard) {
        throw new Error("Confira os dados do cartão e tente novamente.")
      }
      this.credentialTarget.value = result.encryptedCard
      if (this.hasCardLast4Target) this.cardLast4Target.value = cardNumber.slice(-4)
    } catch (error) {
      event.preventDefault()
      event.stopPropagation()
      this.showError(error.message)
    }
  }

  parseExpiration(value) {
    const text = value.trim()
    const date = text.match(/^\d{1,2}[/.-](\d{1,2})[/.-](\d{4})$/)
    const monthYear = text.match(/^(\d{1,2})[/.-](\d{2}|\d{4})$/)
    const compact = text.replace(/\D/g, "")
    const month = date?.[1] || monthYear?.[1] || compact.slice(0, 2)
    let year = date?.[2] || monthYear?.[2] || compact.slice(2)
    if (year.length === 2) year = `20${year}`
    if (!/^\d{1,2}$/.test(month) || Number(month) < 1 || Number(month) > 12 || !/^\d{4}$/.test(year)) {
      throw new Error("Informe uma validade no formato MM/AA ou DD/MM/AAAA.")
    }
    return { month: month.padStart(2, "0"), year }
  }

  loadSdk() {
    if (window.PagSeguro || document.querySelector("script[data-pagbank-sdk]")) return
    const script = document.createElement("script")
    script.src = "https://assets.pagseguro.com.br/checkout-sdk-js/rc/dist/browser/pagseguro.min.js"
    script.dataset.pagbankSdk = "true"
    document.head.appendChild(script)
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  hideError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
  }
}
